import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { signalRService, NotificationData, ChatNotificationData } from '../services/signalRService';
import { fetchNotifications, fetchUnreadCount, markNotificationAsRead, Notification } from '../utils/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  clearNotifications: () => void;
  onLeadUpdate: (callback: (leadId: string, updateType: 'assigned' | 'transferred') => void) => void;
  offLeadUpdate: (callback: (leadId: string, updateType: 'assigned' | 'transferred') => void) => void;
  onConversationUpdate: (callback: (conversationId: string, updateType: 'assigned' | 'transferred') => void) => void;
  offConversationUpdate: (callback: (conversationId: string, updateType: 'assigned' | 'transferred') => void) => void;
  onChatNotification: (callback: (chatNotification: ChatNotificationData) => void) => void;
  offChatNotification: (callback: (chatNotification: ChatNotificationData) => void) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [leadUpdateCallbacks, setLeadUpdateCallbacks] = useState<((leadId: string, updateType: 'assigned' | 'transferred') => void)[]>([]);
  const [conversationUpdateCallbacks, setConversationUpdateCallbacks] = useState<((conversationId: string, updateType: 'assigned' | 'transferred') => void)[]>([]);
  const [chatNotificationCallbacks, setChatNotificationCallbacks] = useState<((chatNotification: ChatNotificationData) => void)[]>([]);
  const { user, token } = useAuth();

  // Keep latest values without re-creating handlers
  const leadUpdateCallbacksRef = useRef(leadUpdateCallbacks);
  const conversationUpdateCallbacksRef = useRef(conversationUpdateCallbacks);
  const chatNotificationCallbacksRef = useRef(chatNotificationCallbacks);
  const userIdRef = useRef<string | undefined>(user?.id);

  useEffect(() => {
    leadUpdateCallbacksRef.current = leadUpdateCallbacks;
  }, [leadUpdateCallbacks]);

  useEffect(() => {
    conversationUpdateCallbacksRef.current = conversationUpdateCallbacks;
  }, [conversationUpdateCallbacks]);

  useEffect(() => {
    chatNotificationCallbacksRef.current = chatNotificationCallbacks;
  }, [chatNotificationCallbacks]);

  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  const refreshNotifications = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const [notificationsData, unreadData] = await Promise.all([
        fetchNotifications(),
        fetchUnreadCount()
      ]);

      // Validate and fix notification dates
      const validatedNotifications = notificationsData.map(notification => ({
        ...notification,
        createdAt: notification.createdAt || new Date().toISOString()
      }));

      setNotifications(validatedNotifications);
      setUnreadCount(unreadData.count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);

      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );

      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const onLeadUpdate = useCallback((callback: (leadId: string, updateType: 'assigned' | 'transferred') => void) => {
    setLeadUpdateCallbacks(prev => [...prev, callback]);
  }, []);

  const offLeadUpdate = useCallback((callback: (leadId: string, updateType: 'assigned' | 'transferred') => void) => {
    setLeadUpdateCallbacks(prev => prev.filter(cb => cb !== callback));
  }, []);

  const onConversationUpdate = useCallback((callback: (conversationId: string, updateType: 'assigned' | 'transferred') => void) => {
    setConversationUpdateCallbacks(prev => [...prev, callback]);
  }, []);

  const offConversationUpdate = useCallback((callback: (conversationId: string, updateType: 'assigned' | 'transferred') => void) => {
    setConversationUpdateCallbacks(prev => prev.filter(cb => cb !== callback));
  }, []);

  const onChatNotification = useCallback((callback: (chatNotification: ChatNotificationData) => void) => {
    setChatNotificationCallbacks(prev => [...prev, callback]);
  }, []);

  const offChatNotification = useCallback((callback: (chatNotification: ChatNotificationData) => void) => {
    setChatNotificationCallbacks(prev => prev.filter(cb => cb !== callback));
  }, []);

  // Handle incoming SignalR notifications
  const handleNewNotification = useCallback((notificationData: NotificationData) => {
    console.log('🎯 NotificationContext - Processing notification:', notificationData);
    const newNotification: Notification = {
      id: notificationData.id,
      title: notificationData.title,
      message: notificationData.message,
      userId: userIdRef.current || '',
      leadId: notificationData.leadId,
      conversationId: notificationData.conversationId,
      type: notificationData.type,
      isRead: notificationData.isRead,
      createdAt: notificationData.createdAt || new Date().toISOString()
    };

    setNotifications(prev => [newNotification, ...prev]);

    if (!notificationData.isRead) {
      setUnreadCount(prev => prev + 1);

      // Show toast notification
      toast.success(notificationData.message, {
        duration: 5000,
        style: {
          background: 'linear-gradient(135deg, #667eea 0%, #1a1a1a 100%)',
        }
      });
    }

    // Trigger lead update callbacks for assignment/transfer notifications
    if (notificationData.leadId && (notificationData.type === 'LEAD_ASSIGNED' || notificationData.type === 'LEAD_TRANSFERRED')) {
      const updateType = notificationData.type === 'LEAD_ASSIGNED' ? 'assigned' : 'transferred';
      console.log(`🔄 Triggering lead update callbacks for ${updateType}:`, notificationData.leadId);
      console.log(`📋 Number of lead update callbacks:`, leadUpdateCallbacksRef.current.length);
      leadUpdateCallbacksRef.current.forEach(callback => callback(notificationData.leadId!, updateType));
    }

    // Trigger conversation update callbacks for assignment/transfer notifications
    if (notificationData.conversationId && (notificationData.type === 'CONVERSATION_ASSIGNED' || notificationData.type === 'CONVERSATION_TRANSFERRED')) {
      const updateType = notificationData.type === 'CONVERSATION_ASSIGNED' ? 'assigned' : 'transferred';
      console.log(`🔄 Triggering conversation update callbacks for ${updateType}:`, notificationData.conversationId);
      console.log(`📋 Number of conversation update callbacks:`, conversationUpdateCallbacksRef.current.length);
      conversationUpdateCallbacksRef.current.forEach(callback => callback(String(notificationData.conversationId!), updateType));
    }
  }, []);

  // Handle incoming SignalR chat notifications
  const handleChatNotification = useCallback((chatNotificationData: ChatNotificationData) => {
    console.log('💬 NotificationContext - Processing chat notification:', chatNotificationData);

    // Show toast notification for new chat messages
    toast.success(`Nova mensagem de ${chatNotificationData.chatName || chatNotificationData.userPhone}`, {
      duration: 3000,
      style: {
        background: 'linear-gradient(135deg, #667eea 0%, #1a1a1a 100%)',
      }
    });

    // Trigger chat notification callbacks
    console.log(`💬 Triggering chat notification callbacks:`, chatNotificationData.chatId);
    console.log(`📋 Number of chat notification callbacks:`, chatNotificationCallbacksRef.current.length);
    chatNotificationCallbacksRef.current.forEach(callback => callback(chatNotificationData));
  }, []);

  // Initialize SignalR connection and notifications when user logs in
  useEffect(() => {
    if (user && token) {
      // Start SignalR connection
      signalRService.startConnection(token);

      // Subscribe to notifications
      signalRService.onNotification(handleNewNotification);
      signalRService.onChatNotification(handleChatNotification);

      // Load initial notifications
      refreshNotifications();
    } else {
      // Stop SignalR connection and clear notifications when user logs out
      signalRService.stopConnection();
      clearNotifications();
    }

    return () => {
      signalRService.offNotification(handleNewNotification);
      signalRService.offChatNotification(handleChatNotification);
    };
  }, [user, token, handleNewNotification, handleChatNotification, refreshNotifications, clearNotifications]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      signalRService.stopConnection();
    };
  }, []);

  const contextValue: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    refreshNotifications,
    clearNotifications,
    onLeadUpdate,
    offLeadUpdate,
    onConversationUpdate,
    offConversationUpdate,
    onChatNotification,
    offChatNotification
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};